import type { ApiRentalTopUpQuote, ApiRentalExtensionQuote } from 'src/types/rental';

import { useQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';
import { LIVE_STALE_TIME } from './staleness';
import { useRequestSession } from './useRequestSession';

/**
 * The two amounts a renter can be asked for after paying, both quoted by the contract
 * (rental-clock v2.2 §2.3 / §2.7) and proxied by the api. The app deliberately does NOT
 * compute either one: the contract rejects a top-up above its own quote, and it prices an
 * extension by the tier band the extension's own length falls into — a rule the screen
 * would have to duplicate to get right, and would eventually get wrong.
 *
 * Read under the account session and keyed by it like every read of a person's own deal
 * (app#248), although a quote is kept only while its modal is open.
 */

/** What `topUpDeposit()` accepts right now: overdue debt + deposit gap. */
export function useRentalTopUpQuote(dealId: string, enabled: boolean) {
  const session = useRequestSession();

  return useQuery({
    queryKey: QueryKeys.forSession(QueryKeys.rentalTopUpQuote(dealId), session),
    queryFn: async (): Promise<ApiRentalTopUpQuote> => {
      const response = await axios.get<ApiRentalTopUpQuote>(`/buyer/rental-deals/${dealId}/top-up-quote`);
      return response.data;
    },
    enabled: enabled && !!dealId,
    // The amount moves with the clock (a new overdue day starts, a cron charges): never
    // hand the wallet a cached number.
    staleTime: LIVE_STALE_TIME,
    gcTime: 0,
  });
}

/** What extending by `days` whole days costs, priced by the contract's tier grid. */
export function useRentalExtensionQuote(dealId: string, days: number, enabled: boolean) {
  const session = useRequestSession();

  return useQuery({
    queryKey: QueryKeys.forSession(QueryKeys.rentalExtensionQuote(dealId, days), session),
    queryFn: async (): Promise<ApiRentalExtensionQuote> => {
      const response = await axios.get<ApiRentalExtensionQuote>(
        `/buyer/rental-deals/${dealId}/extension-quote`,
        { params: { days } }
      );
      return response.data;
    },
    enabled: enabled && !!dealId && Number.isInteger(days) && days > 0,
    staleTime: LIVE_STALE_TIME,
    gcTime: 0,
  });
}
